/**
 * @description 获取音频设备及流
 */

export class AudioGetter {
  private _callback: ((event: MediaDeviceInfo) => void) | undefined;
  private _currentMic: MediaDeviceInfo | undefined;
  /**
   * 获取系统音频流
   * @returns
   */
  public getSystemAudioStream = async (): Promise<MediaStream | undefined> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const systemStream = await (navigator.mediaDevices as any)
      .getUserMedia({
        audio: {
          mandatory: {
            chromeMediaSource: "desktop",
          },
          echoCancellation: true, // 启用回声消除
          noiseSuppression: true, // 启用噪声抑制
          autoGainControl: true, // 启用自动增益控制
        },
        // 必须也要设置，不然就崩了
        video: {
          mandatory: {
            chromeMediaSource: "desktop",
          },
        },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        console.log("[获取系统音频流异常]", err);
        return undefined;
      });
    return systemStream;
  };

  /**
   * 获取麦克风音频流
   * @param deviceId 麦克风设备id
   * @returns
   */
  public getMicrophoneAudioStream = async (
    deviceId?: string
  ): Promise<MediaStream | undefined> => {
    const microphoneStream = await navigator.mediaDevices
      .getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          sampleSize: 16,
          deviceId,
          echoCancellation: true, // 启用回声消除
          noiseSuppression: true, // 启用噪声抑制
          autoGainControl: true, // 启用自动增益控制
        },
      })
      .catch((err) => {
        console.log("[获取麦克风音频流异常]", err);
        return undefined;
      });
    return microphoneStream;
  };

  /**
   * 获取默认麦克风设备
   * @returns
   */
  public getDefautMicrophone = async (): Promise<
    MediaDeviceInfo | undefined
  > => {
    const mic = await this._getDefaultMic();
    this._currentMic = mic;
    return mic;
  };

  /**
   * 监听麦克风设备变化
   * @param callback
   */
  public listenMicrophoneChange = (
    callback: (event: MediaDeviceInfo) => void
  ) => {
    this._callback = callback;
    navigator.mediaDevices.addEventListener(
      "devicechange",
      this._handleDeviceChange
    );
  };

  /**
   * 取消监听麦克风设备变化
   * @param callback
   */
  public offLiestenMicrophoneChange = () => {
    this._callback = undefined;
    navigator.mediaDevices.removeEventListener(
      "devicechange",
      this._handleDeviceChange
    );
  };

  public tryToGetpermission = async () => {
    try {
      if (navigator.permissions) {
        const name = "microphone" as unknown as PermissionName;
        const res = await navigator.permissions.query({ name });
        if (res.state === "granted") {
          return true;
        }
        if (res.state === "denied") {
          return false;
        }
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      // 没有流或者流是不活跃的，则表示可能无权限
      if (!mediaStream || !mediaStream.active) {
        return false;
      }
      const tracks = mediaStream.getTracks();
      tracks.forEach((track) => {
        track.stop();
      });
      return true;
    } catch (error) {
      console.log("tryToGetpermission", error);
      return false;
    }
  };

  /**
   * 获取麦克风设备列表
   * @returns
   */
  public getMicList = async () => {
    const mics = await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) =>
        devices.filter((device) => device.kind === "audioinput")
      )
      .catch((error) => {
        console.log("[获取麦克风设备异常]", error);
        return [];
      });
    return mics;
  };

  private _getDefaultMic = async () => {
    const mics = await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) =>
        devices.filter((device) => device.kind === "audioinput")
      )
      .catch((error) => {
        console.log("[获取麦克风设备异常]", error);
        return [];
      });
    if (mics.length === 0) return undefined;
    if (mics.length === 1) return mics[0];
    const choosedMic = mics.find((mic) =>
      mic.label.toLowerCase().startsWith("default")
    );
    return choosedMic || mics[0];
  };

  private _handleDeviceChange = async () => {
    const device = await this._getDefaultMic();
    if (!device) return;
    // electron 上获取的默认设备的id都是 Default，所以这里不判断ID，判断label
    if (this._currentMic?.label === device?.label) {
      return;
    }
    this._currentMic = device;
    this._callback && this._callback(device);
  };
}
